/** Static map assets (cached by the browser; versioned by file hash in production builds). */
import { decodeWorld, type LoadedWorld, type WorldMeta } from '@sa/mapdata';
import binUrl from '@sa/mapdata/generated/world.v1.bin?url';
import coastUrl from '@sa/mapdata/generated/coast.v1.bin?url';
import metaUrl from '@sa/mapdata/generated/world.v1.meta.json?url';

export interface WorldAssets {
  world: LoadedWorld;
  /** raw files, for handing a copy to the offline worker */
  raw: { bin: ArrayBuffer; meta: WorldMeta; coast: ArrayBuffer };
}

let cached: Promise<WorldAssets> | null = null;

export function loadWorldAssets(): Promise<WorldAssets> {
  cached ??= (async () => {
    const get = async (url: string) => {
      const r = await fetch(url);
      if (!r.ok) throw new Error(`map asset ${url}: HTTP ${r.status}`);
      return r;
    };
    const [bin, meta, coast] = await Promise.all([
      get(binUrl).then((r) => r.arrayBuffer()),
      get(metaUrl).then((r) => r.json() as Promise<WorldMeta>),
      get(coastUrl).then((r) => r.arrayBuffer()),
    ]);
    return { world: decodeWorld(bin, meta, coast), raw: { bin, meta, coast } };
  })();
  return cached;
}

/** Fresh copies of the raw buffers (transferring them to a worker detaches the originals). */
export function rawCopy(a: WorldAssets): { bin: ArrayBuffer; meta: WorldMeta; coast: ArrayBuffer } {
  return { bin: a.raw.bin.slice(0), meta: a.raw.meta, coast: a.raw.coast.slice(0) };
}
